import React from 'react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * The pill that carries a status in a table cell, and the bare dot + label that
 * carries a secondary one beside it.
 *
 * One map decides what every status looks like, so the bookings table, the
 * detail panel and the slot board cannot drift apart. Add a status here rather
 * than colouring one at a call site — a status that means the same thing in two
 * places must look the same in both.
 */

type Tone = 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'violet' | 'brand';

const TONE_PILL: Record<Tone, string> = {
  success: 'bg-flup-success-soft text-flup-success',
  info: 'bg-flup-info-soft text-flup-info',
  warning: 'bg-flup-warning-soft text-flup-warning',
  danger: 'bg-flup-danger-soft text-flup-danger',
  neutral: 'bg-flup-neutral-soft text-flup-neutral',
  violet: 'bg-flup-violet-soft text-flup-violet',
  brand: 'bg-flup-brand-soft text-flup-brand-ink',
};

const TONE_DOT: Record<Tone, string> = {
  success: 'bg-flup-success',
  info: 'bg-flup-info',
  warning: 'bg-flup-warning',
  danger: 'bg-flup-danger',
  neutral: 'bg-flup-neutral',
  violet: 'bg-flup-violet',
  brand: 'bg-flup-brand',
};

/** Canonical label + tone for every status string the API can hand back. */
const STATUS: Record<string, { label: string; tone: Tone }> = {
  // Bookings & payments
  confirmed: { label: 'Confirmed', tone: 'success' },
  completed: { label: 'Completed', tone: 'success' },
  success: { label: 'Success', tone: 'success' },
  processed: { label: 'Processed', tone: 'success' },
  paid: { label: 'Paid', tone: 'success' },
  pending: { label: 'Pending', tone: 'warning' },
  initiated: { label: 'Initiated', tone: 'warning' },
  processing: { label: 'Processing', tone: 'info' },
  inprogress: { label: 'In progress', tone: 'info' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  canceled: { label: 'Cancelled', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'danger' },
  refunded: { label: 'Refunded', tone: 'violet' },
  rescheduled: { label: 'Rescheduled', tone: 'violet' },

  // Slots
  available: { label: 'Open', tone: 'success' },
  booked: { label: 'Booked', tone: 'info' },
  hold: { label: 'On hold', tone: 'warning' },
  unavailable: { label: 'Blocked', tone: 'danger' },
  blocked: { label: 'Blocked', tone: 'danger' },
  expired: { label: 'Passed', tone: 'neutral' },
  expiring: { label: 'Closing', tone: 'warning' },

  // Venues
  live: { label: 'Live', tone: 'success' },
  verified: { label: 'Verified', tone: 'success' },
  unverified: { label: 'Pending review', tone: 'warning' },
  draft: { label: 'Draft', tone: 'neutral' },
  active: { label: 'Active', tone: 'success' },
  inactive: { label: 'Inactive', tone: 'neutral' },
  expiredplan: { label: 'Expired', tone: 'danger' },
};

const resolve = (status?: string | null, tone?: Tone, label?: string) => {
  const key = String(status || '').toLowerCase().replace(/[\s_-]/g, '');
  const hit = STATUS[key];
  return {
    label: label ?? hit?.label ?? (status ? String(status) : '—'),
    tone: tone ?? hit?.tone ?? 'neutral',
  };
};

export interface StatusBadgeProps {
  status?: string | null;
  /** Override the canonical label without losing the colour mapping. */
  label?: string;
  /** Force a tone, for a status the map doesn't know. */
  tone?: Tone;
  size?: 'sm' | 'md';
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, label, tone, size = 'md', className }) => {
  const resolved = resolve(status, tone, label);
  const compact = size === 'sm';

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-flup-xs whitespace-nowrap',
        compact ? 'h-5 px-1.5' : 'h-6 px-2.5',
        TONE_PILL[resolved.tone],
        className,
      )}
    >
      <Text variant="micro" tone="inherit" as="span">
        {resolved.label}
      </Text>
    </span>
  );
};

/** Coloured dot + label, for the secondary status beside a pill. */
export const StatusDot: React.FC<StatusBadgeProps> = ({ status, label, tone, className }) => {
  const resolved = resolve(status, tone, label);
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap', className)}>
      <span className={cn('w-1.5 h-1.5 rounded-pill shrink-0', TONE_DOT[resolved.tone])} aria-hidden />
      <Text variant="caption" tone="secondary" as="span">
        {resolved.label}
      </Text>
    </span>
  );
};

export default React.memo(StatusBadge);
